<template>
    <form novalidate class="md-layout" @submit.prevent="validateForm()">
        <md-content class="card md-layout-item md-size-100 md-small-size-100">
            <div class="md-flex-center-center md-form-input md-relative">
                <h1 class="md-title md-form-title">{{ `${$t('views.admin.templates.edit.title')} ${form.key}` }}</h1>
                <md-card class="md-form-card md-mt-70">
                    <div v-if="form.templateType==='COMBINE'">
                        <div class="md-layout-item">
                            <AdsignChips :chips="chips" @chipEntered="onChipSubjectClick" />
                        </div>
                        <div class="md-layout md-gutter">
                            <div class="md-layout-item md-size-100">
                                <md-field :class="getValidationClass('subject-content')">
                                    <label for="subject-content">
                                        {{ $t(`views.admin.templates.edit.template.subject.content`) }}
                                    </label>
                                    <md-input name="subject-content" id="subject-content" v-model="form.subject.content"
                                              :disabled="sending" />
                                    <span class="md-error" v-if="!$v.form.subject.content.required">{{ $t('validation.required') }}</span>
                                    <server-error v-else :server-errors="serverErrors" field-name="subject-content" />
                                </md-field>
                            </div>
                            <div class="md-layout-item">
                                <AdsignChips :chips="chips" @chipEntered="onChipBodyClick" />
                            </div>

                            <div class="md-layout-item md-size-100">
                                <md-field :class="getValidationClass('body-content')">
                                    <label for="body-content">
                                        {{ $t(`views.admin.templates.edit.template.body.content`) }}
                                    </label>
                                    <md-textarea ref="bodyContent" name="body-content" id="body-content"
                                                 v-model="form.body.content"
                                                 :disabled="sending" />
                                    <span class="md-error" v-if="!$v.form.body.content.required">{{ $t('validation.required') }}</span>
                                    <server-error v-else :server-errors="serverErrors" field-name="body-content" />
                                </md-field>
                            </div>
                        </div>
                    </div>
                    <div v-if="form.templateType==='BASIC'">
                        <div class="md-layout md-gutter">
                            <div class="md-layout-item">
                                <AdsignChips :chips="chips" @chipEntered="onChipMessageClick" />
                            </div>

                            <div class="md-layout-item md-size-100">
                                <md-field :class="getValidationClass('message-content')">
                                    <label for="message-content">
                                        {{ $t(`views.admin.templates.edit.template.body.content`) }}
                                    </label>
                                    <md-textarea ref="messageContent" name="message-content" id="message-content"
                                                 v-model="form.message.content"
                                                 :disabled="sending" />
                                    <span class="md-error" v-if="!$v.form.message.content.required">{{ $t('validation.required') }}</span>
                                    <server-error v-else :server-errors="serverErrors" field-name="message-content" />
                                </md-field>
                            </div>
                        </div>
                    </div>
                    <div class="md-layout-item md-size-100 md-flex-right-align md-mt-30">
                        <md-button class="md-form-btn-cancel md-mr-30" @click="back" :disabled="sending">{{ $t('back') }}</md-button>
                        <md-button type="submit" class="md-header-btn" :disabled="sending">
                            {{ $t('views.admin.privileges.group.create.form.save') }}
                        </md-button>
                    </div>
                </md-card>
            </div>
            <md-progress-bar md-mode="indeterminate" v-if="sending" />
        </md-content>
    </form>
</template>

<script>
  import {required} from 'vuelidate/lib/validators';
  import {templatesEndpoint} from '@/services/adsign-backend/endpoints';
  import serverSideValidationErrors from '@/mixins/serverSideValidationErrors';
  import ServerError from '@/components/ServerError';
  import AdsignChips from '@/components/AdsignChips';

  export default {
    name: 'TemplateForm',
    mixins: [serverSideValidationErrors],
    components: {AdsignChips, ServerError},
    props: {
      id: {
        type: [String, Number],
        default: undefined
      }
    },
    data: () => ({
      sending: false,
      chips: [],
      form: {
        id: null,
        key: '',
        templateType: '',
        subject: {
          key: '',
          content: ''
        },
        body: {
          key: '',
          content: ''
        },
        message: {
          key: '',
          content: ''
        }
      }
    }),
    mounted() {
      this.loadTemplate();
      this.loadChips();
    },
    validations() {
      if (this.form.templateType === 'BASIC') {
        return {
          form: {
            id: {
              required
            },
            key: {
              required
            },
            templateType: {
              required
            },
            message: {
              key: {
                required
              },
              content: {
                required
              }
            }
          }
        }
      } else {
        return {
          form: {
            id: {
              required
            },
            key: {
              required
            },
            templateType: {
              required
            },
            subject: {
              key: {
                required
              },
              content: {
                required
              }
            },
            body: {
              key: {
                required
              },
              content: {
                required
              }
            }
          }
        }
      }
    },
    methods: {
      back() {
        this.$router.push(`/configurations/templates`);
      },
      loadTemplate() {
        templatesEndpoint.getTemplate(this.id).then(r => this.initForm(r.data));
      },
      loadChips() {
        templatesEndpoint.getTemplatePlaceholders(this.id).then(r => {
          this.chips = Object.keys(r.data.placeholders).map(key => {
            if (typeof r.data.placeholders[key] === 'string') {
              return ({
                name: key.replace(/\./g, '_'),
                code: r.data.placeholders[key]
              });
            } else {
              return ({
                name: key.replace(/\./g, '_'),
                fields: Object.keys(r.data.placeholders[key]).map(fieldKey => ({
                  name: fieldKey.replace(/\./g, '_'),
                  code: r.data.placeholders[key][fieldKey]
                })).sort((a, b) => ('' + a.name).localeCompare(b.name))
              });
            }
          });
        });
      },
      initForm(result) {
        if (result.templateType === 'BASIC') {
          this.initBasicForm(result)

        } else if (result.templateType === 'COMBINE') {
          this.initEmailForm(result)
        }
      },
      initEmailForm(result) {
        this.form = {
          id: result.id,
          key: result.key,
          templateType: result.templateType,
          subject: {
            key: result.subject.key,
            content: result.subject.content
          },
          body: {
            key: result.body.key,
            content: result.body.content
          }
        }
      },

      initBasicForm(result) {
        this.form = {
          id: result.id,
          key: result.key,
          templateType: result.templateType,
          message: {
            key: result.message.key,
            content: result.message.content
          }
        }
      },
      validateForm() {
        this.$v.$touch();

        if (!this.$v.$invalid) {
          this.sending = true;
          templatesEndpoint.updateTemplate(this.id, this.form)
              .then(response => {
                this.back();
              })
              .catch(this.handleServerValidationError)
              .finally(() => {
                    this.sending = false;
                  }
              );
        }
      },
      onChipBodyClick(event) {
        this.insertChip(event.code, 'body-content', (v) => this.form.body.content = v)
      },
      onChipSubjectClick(event) {
        this.insertChip(event.code, 'subject-content', (v) => this.form.subject.content = v)
      },
      onChipMessageClick(event) {
        this.insertChip(event.code, 'message-content', (v) => this.form.message.content = v)
      },
      insertChip(value, componentName, valueSetter) {
        var textArea = document.getElementsByName(componentName)[0];
        var startPos = textArea.selectionStart,
            endPos = textArea.selectionEnd,
            cursorPos = startPos,
            tmpStr = textArea.value;

        if (value === null) {
          return;
        }

        valueSetter(tmpStr.substring(0, startPos) + value + tmpStr.substring(endPos, tmpStr.length));

        setTimeout(() => {
          textArea.focus();
          cursorPos += value.length;
          textArea.selectionStart = textArea.selectionEnd = cursorPos;
        }, 10);
      }
    }
  }
</script>

